<script lang="ts" setup>
import { collabsAndCommunity } from '~~/const/collabs'

import VisitBounty from '~/assets/images/engagement/spend-percentage.svg?component'
import BelongDocumentation from '~/assets/images/engagement/belong-documentation.svg?component'

import IconInstantSettlement from '~/assets/images/engagement/icon-instant-settlement.svg?component'
import IconLowerDepositFees from '~/assets/images/engagement/icon-lower-deposit-fees.svg?component'
import IconNetworkBenefits from '~/assets/images/engagement/icon-network-benefits.svg?component'
import IconLowerProcessingFees from '~/assets/images/engagement/icon-lower-processing-fees.svg?component'
import IconFlexibility from '~/assets/images/engagement/icon-flexibility.svg?component'
import IconCustomerDiscount from '~/assets/images/engagement/icon-customer-discount.svg?component'

import { LazyMotion, m, domAnimation } from 'motion-v'

definePageMeta({
  layout: 'menu',
  alias: ['/checkin'],
})

const {
  public: { siteUrl },
} = useRuntimeConfig()

const { t, locale } = useI18n()
const { width } = useWindowSize()

const checkInSeoTitle =
  'Belong CheckIn verifies venue visits with QR and NFC rewards'
const checkInSeoDescription = t('a_blockchain_powered_affiliate_network')

useSeoMeta({
  title: checkInSeoTitle,
  description: checkInSeoDescription,
  ogTitle: checkInSeoTitle,
  ogDescription: checkInSeoDescription,
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1000',
  ogImageHeight: '300',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: checkInSeoTitle,
  twitterDescription: checkInSeoDescription,
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

const tab = ref('venues')

const changeTab = (newTab: string) => {
  tab.value = newTab
}

const scrollToSection = (id: string, value: string) => {
  if (value === 'venues') {
    changeTab('venues')
  }
  if (value === 'promoters') {
    changeTab('promoters')
  }

  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

const buttons = [
  {
    text: t('for-venues'),
    link: 'https://docs.belong.net/belong-checkin/venue-partner',
    click: () => scrollToSection('belong', 'venues'),
  },
  {
    text: t('for-promoters'),
    link: 'https://docs.belong.net/belong-checkin/promoter-earnings-guide',
    click: () => scrollToSection('belong', 'promoters'),
  },
]

const rewards = [
  {
    icon: VisitBounty,
    title: 'rewards.fixed-reward.title',
    link: 'https://docs.belong.net/guides/crypto/checkin',
    description: 'rewards.fixed-reward.description',
  },
  {
    icon: VisitBounty,
    title: 'rewards.spend-percentage.title',
    link: 'https://docs.belong.net/belong-checkin/promoter-earnings-guide',
    description: 'rewards.spend-percentage.description',
  },
]

const features = [
  {
    title: 'features.long-token.title',
    sup: 'features.long-token.sup',
    description: 'features.long-token.description',
    class:
      'lg:row-span-2 lg:bg-[linear-gradient(153.23deg,rgba(21,21,21,0.01)_5.53%,#151515_65.04%)] lg:backdrop-blur-2xl lg:pl-6.5',
    image: '/images/engagement/long-token.png',
    imageClass: 'w-50 h-67.5 -top-15 lg:w-63.5 lg:top-11 lg:right-2.5',
  },
  {
    title: 'features.web.title',
    description: 'features.web.description',
    class: 'lg:bg-[#0D0D0D] lg:pl-5',
    image: '/images/engagement/web-2.5.png',
    imageClass:
      'top-0 left-0 rotate-180 lg:bottom-0 lg:-right-0 lg:w-65 lg:top-auto lg:rotate-0 lg:left-auto',
  },
  {
    title: 'features.credit-tokens.title',
    sup: 'features.credit-tokens.sup',
    description: 'features.credit-tokens.description',
    class: 'lg:bg-[#0D0D0D] lg:col-start-2 lg:row-start-2 lg:pl-5',
    image: '/images/engagement/long-token.png',
    imageClass:
      'right-0 top-0 lg:h-52.25 lg:object-contain lg:bottom-0 lg:right-7.5',
  },
]

const payoutSections = [
  {
    title: 'payout-sections.dual-payouts.title',
    description: 'payout-sections.dual-payouts.description',
  },
  {
    title: 'payout-sections.venue-fees.title',
    description: 'payout-sections.venue-fees.description',
  },
  {
    title: 'payout-sections.platform-referrals.title',
    description: 'payout-sections.platform-referrals.description',
  },
]

const payoutSteps = [
  {
    id: 1,
    title: 'payout-steps.step-1.title',
    subtitle: 'payout-steps.step-1.subtitle',
    description: 'payout-steps.step-1.description',
    options: ['payout-steps.step-1.options.option-1'],
  },
  {
    id: 2,
    title: 'payout-steps.step-2.title',
    subtitle: 'payout-steps.step-2.subtitle',
    description: 'payout-steps.step-2.description',
    options: [
      'payout-steps.step-2.options.option-a',
      'payout-steps.step-2.options.option-b',
    ],
  },
]

const roadmapFeatures = [
  {
    icon: '/images/engagement/bnb-chain.png',
    descriptionKey: 'roadmap-features.bnb-chain',
  },
  {
    icon: '/images/engagement/ai-powered.png',
    descriptionKey: 'roadmap-features.ai-powered',
  },
  {
    icon: '/images/engagement/voice-agent.png',
    descriptionKey: 'roadmap-features.voice-agent',
  },
  {
    icon: '/images/engagement/newbie-friendly.png',
    descriptionKey: 'roadmap-features.newbie-friendly',
  },
]

const benefits = [
  {
    icon: IconInstantSettlement,
    title: t('benefits.instant-settlement.title'),
    description: t('benefits.instant-settlement.description'),
    iconClass: '',
  },
  {
    icon: IconLowerDepositFees,
    title: t('benefits.lower-deposit-fees.title'),
    description: t('benefits.lower-deposit-fees.description'),
    iconClass: 'min-w-6.5',
  },
  {
    icon: IconNetworkBenefits,
    title: t('benefits.network-benefits.title'),
    description: t('benefits.network-benefits.description'),
    iconClass: '',
  },
  {
    icon: IconLowerProcessingFees,
    title: t('benefits.lower-processing-fees.title'),
    description: t('benefits.lower-processing-fees.description'),
    iconClass: 'min-w-6',
  },
  {
    icon: IconFlexibility,
    title: t('benefits.flexibility.title'),
    description: t('benefits.flexibility.description'),
    iconClass: '',
  },
  {
    icon: IconCustomerDiscount,
    title: t('benefits.customer-discount.title'),
    i18nKey: t('benefits.customer-discount.three-percent'),
    description: t('benefits.customer-discount.description-rest'),
    iconClass: '',
  },
]

const roadmap = [
  {
    year: 'roadmap-plan.foundation.year',
    title: 'roadmap-plan.foundation.title',
    points: [
      'roadmap-plan.foundation.points.0',
      'roadmap-plan.foundation.points.1',
      'roadmap-plan.foundation.points.2',
    ],
  },
  {
    year: 'roadmap-plan.expansion.year',
    title: 'roadmap-plan.expansion.title',
    points: [
      'roadmap-plan.expansion.points.0',
      'roadmap-plan.expansion.points.1',
      'roadmap-plan.expansion.points.2',
      'roadmap-plan.expansion.points.3',
    ],
  },
  {
    year: 'roadmap-plan.platform-evolution.year',
    title: 'roadmap-plan.platform-evolution.title',
    points: [
      'roadmap-plan.platform-evolution.points.0',
      'roadmap-plan.platform-evolution.points.1',
      'roadmap-plan.platform-evolution.points.2',
      'roadmap-plan.platform-evolution.points.3',
    ],
  },
]
</script>

<template>
  <div>
    <div class="flex flex-col pt-24 pb-11 xl:gap-15 relative">
      <div
        class="flex flex-col justify-between max-w-278.5 w-full lg:mx-auto lg:flex-row"
      >
        <div
          class="flex flex-col gap-10 lg:gap-15 text-center px-5 lg:pr-0 lg:max-w-110 lg:text-left lg:justify-center min-[1200px]:pt-36 min-[1200px]:!justify-start min-[1200px]:px-0"
        >
          <div class="flex flex-col gap-3 lg:gap-4">
            <p
              class="font-extrabold text-[2.375rem] lg:text-[2.75rem] leading-11 tracking-wider"
            >
              {{ t('hero.title') }}
            </p>

            <p class="text-[#B9B9B9] text-base tracking-wider">
              {{ t('hero.description') }}
            </p>
          </div>

          <div class="flex flex-col items-center gap-4 lg:items-start">
            <div
              class="flex flex-col min-[1200px]:flex-row gap-4 w-full sm:w-auto"
            >
              <NuxtLink
                to="https://github.com/belongnet"
                target="_blank"
                class="bg-[#7540EE] rounded-lg flex gap-2.5 py-2 px-5 items-center justify-center cursor-pointer"
              >
                <Icon name="mdi:github" class="text-white text-[1.625rem]" />
                <p class="font-semibold text-base tracking-wide">GitHub</p>
              </NuxtLink>

              <NuxtLink
                target="_blank"
                to="https://docs.belong.net/belong-checkin/checkin"
                class="border-[#7540EE] border-solid border-1 rounded-lg flex gap-1.5 py-2 px-3.5 items-center justify-center cursor-pointer"
              >
                <BelongDocumentation />
                <p class="font-medium tracking-wide text-[#7540EE]">
                  {{ t('documentation') }}
                </p>
              </NuxtLink>
            </div>

            <div
              class="flex flex-row flex-wrap items-center gap-4 justify-center lg:justify-start"
            >
              <NuxtLink
                v-for="button in buttons"
                :key="button.text"
                :to="button.link"
                target="_blank"
                class="text-base tracking-wide p-0.25 rounded-full cursor-pointer"
                :class="locale === 'en' ? 'font-semibold' : ''"
                style="
                  background: linear-gradient(90deg, #4d61fd 0%, #bb2bbf 100%);
                "
              >
                <div
                  class="bg-[#151515] rounded-full py-1 px-6.5 lg:w-auto lg:px-4.5 cursor-pointer"
                >
                  {{ button.text }}
                </div>
              </NuxtLink>
            </div>
          </div>
        </div>

        <NuxtImg
          src="/images/engagement/phone_image.png"
          loading="lazy"
          class="h-91 w-full object-contain lg:h-126 lg:w-148.5 mx-auto mt-6.5 lg:m-0"
        />
      </div>

      <NuxtImg
        src="/images/engagement/bg-top.png"
        class="top-0 absolute left-0 -z-1 lg:hidden"
      />

      <div
        class="w-full h-100 bottom-0 overflow-hidden absolute -z-2 perspective-normal max-w-350 mx-auto flex justify-center left-0 right-0"
      >
        <div class="grid-fade" />
        <div class="grid-lines" />
      </div>
    </div>

    <div class="pt-10">
      <ReusableExchangesList />
    </div>

    <div class="flex flex-col mt-17.5 gap-8 overflow-hidden">
      <p class="text-lg text-center px-9 uppercase">
        {{ t('building-on-existing') }}
      </p>

      <UiCollabsCarousel :data="collabsAndCommunity" />
    </div>

    <div
      class="flex flex-col px-5 max-w-270 w-full justify-between gap-7.5 pt-16 lg:gap-0 lg:pt-30 lg:flex-row-reverse lg:mx-auto lg:items-end min-[1200px]:!px-0"
    >
      <div class="relative">
        <NuxtImg
          src="/images/engagement/pay-only.png"
          class="rounded-2xl h-60 md:h-80 lg:h-110 lg:w-131.5 object-cover w-full object-top"
        />

        <LazyMotion :features="domAnimation">
          <m.div
            :initial="{ opacity: 0, y: 30, scale: 0.95 }"
            :while-in-view="{ opacity: 1, y: 0, scale: 1 }"
            :transition="{ duration: 0.6, ease: 'easeOut' }"
            :viewport="{ once: true, amount: 0.3 }"
            class="bg-[#7540EE99] absolute bottom-22 right-19.5 rounded-lg flex flex-row items-center max-w-50 p-4 gap-2 backdrop-blur-lg"
          >
            <NuxtImg
              src="/images/engagement/pay-only-icon.png"
              class="w-6 h-6"
            />
            <p
              class="text-[0.625rem]"
              :class="locale === 'en' ? 'tracking-tighter' : 'tracking-wide'"
            >
              {{ t('recent-transactions.timothy-spent') }}
            </p>
          </m.div>

          <m.div
            :initial="{ opacity: 0, y: 30, scale: 0.95 }"
            :while-in-view="{ opacity: 1, y: 0, scale: 1 }"
            :transition="{ delay: 0.3, duration: 0.6, ease: 'easeOut' }"
            :viewport="{ once: true, amount: 0.3 }"
            class="bg-[#7540EE99] absolute bottom-4 right-4 rounded-lg flex flex-row items-center max-w-50 p-4 gap-2 backdrop-blur-lg"
          >
            <NuxtImg
              src="/images/engagement/pay-only-icon-long.png"
              class="w-6 h-6"
            />
            <p
              class="text-[0.625rem]"
              :class="locale === 'en' ? 'tracking-tighter' : 'tracking-wide'"
            >
              {{ t('recent-transactions.long-received') }}
            </p>
          </m.div>
        </LazyMotion>
      </div>

      <div class="flex flex-col gap-12.5 lg:gap-17.5">
        <div
          class="flex flex-col gap-3.5 text-center lg:max-w-100 lg:text-left"
        >
          <p class="font-extrabold text-xl lg:text-2xl tracking-wide uppercase">
            {{ t('pay-only-for.title') }}
            <span class="text-[#7540EE]">{{
              t('pay-only-for.highlight')
            }}</span>
          </p>
          <p class="text-[#B9B9B9] tracking-wide">
            {{ t('network-description') }}
          </p>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <div
            v-for="(reward, index) in rewards"
            :key="index"
            class="group flex flex-col gap-3.5 w-full lg:max-w-55 border border-[#2B2B2B] rounded-lg px-3.5 py-3 transition-all duration-300 ease-in-out hover:border-[#7540EE] hover:scale-[1.02]"
          >
            <div class="flex flex-row gap-2">
              <component :is="reward.icon" class="min-w-6" />
              <p class="tracking-tight">
                {{ t(reward.title) }}
              </p>
            </div>

            <p
              class="text-[#B9B9B9] tracking-tight lg:max-w-46.5 h-full leading-4.5"
            >
              {{ t(reward.description) }}
            </p>

            <NuxtLink
              :to="reward.link"
              target="_blank"
              class="text-[#7540EE] flex items-center opacity-100 translate-y-0 md:opacity-0 md:translate-y-1 transition-all duration-300 ease-in-out md:group-hover:opacity-100 md:group-hover:translate-y-0"
            >
              {{ t('learn-more') }}
              <Icon
                name="material-symbols:arrow-right-alt-rounded"
                class="text-xl ml-1"
              />
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
    <div class="relative overflow-hidden mt-10">
      <div
        class="max-w-291 mx-auto w-full flex flex-col pb-10 lg:pt-12.5 lg:px-5 min-[1230px]:!px-0"
      >
        <p
          class="text-[#dfdfdf] text-xl tracking-wide uppercase text-center pb-6 lg:pb-4 lg:text-left lg:pl-6"
        >
          {{ t('token-architecture-overview') }}
        </p>
        <ClientOnly>
          <div
            v-if="width > 1023.9"
            class="grid grid-cols-2 grid-rows-2 h-117.5 gap-x-4 gap-y-3.5"
          >
            <div
              v-for="(item, i) in features"
              :key="item.title"
              :class="item.class"
              class="border-1 border-solid border-[#2B2B2B] rounded-xl pb-5.5 flex flex-col justify-end gap-3 relative overflow-hidden"
            >
              <p
                class="text-2xl font-medium text-[#7540EE] tracking-wide leading-tight uppercase"
              >
                {{ t(item.title) }}
                <sup
                  v-if="item.sup"
                  class="text-sm align-super text-[#7540EE] leading-tight tracking-wide"
                >
                  {{ t(item.sup) }}
                </sup>
              </p>
              <p class="tracking-wide text-[#DEDEDE] max-w-96.5 relative z-1">
                {{ t(item.description) }}
              </p>
              <NuxtImg
                :src="item.image"
                :class="item.imageClass"
                class="absolute"
              />

              <NuxtImg
                v-if="i === 0"
                src="/images/engagement/ellipse.png"
                :alt="item.title"
                class="absolute bottom-0 right-0 w-175 -z-1 h-107 object-cover"
              />
            </div>
          </div>
          <div v-if="width < 1023.9">
            <UCarousel
              v-slot="{ item }"
              dots
              :items="features"
              :ui="{
                container: 'm-auto ps-0 ',
                item: 'basis-auto max-w-80 md:max-w-90 w-full last:mr-4',
                dot: `
              w-4 h-4
              rounded-full
              bg-[#1d1d1d]
              border-4 border-transparent
              data-[state=active]:bg-[#6827ff]
              data-[state=active]:border-[#1d1d1d]
              box-border`,
              }"
            >
              <div class="relative">
                <div
                  class="overflow-hidden before:content-[''] before:absolute before:z-1 before:inset-0 before:rounded-lg before:bg-[linear-gradient(142.08deg,rgba(255,255,255,0.28)_-5.03%,rgba(224,224,224,0.34)_21.3%,rgba(101,84,169,0.08)_49.04%,rgba(218,218,218,0.2)_70.19%)] bg-[linear-gradient(218.16deg,#19161C_-8.71%,rgba(0,0,0,0)_94.29%)] common-gradient relative p-6 rounded-lg min-h-100 h-full before:p-0.25 flex flex-col justify-end"
                >
                  <NuxtImg
                    :src="item.image"
                    :alt="item.title"
                    :class="item.imageClass"
                    class="absolute"
                  />

                  <div class="flex flex-col gap-6">
                    <h3
                      class="text-2xl font-medium text-[#7540EE] tracking-wide leading-tight"
                    >
                      {{ t(item.title) }}
                      <sup
                        v-if="item.sup"
                        class="text-sm align-super text-[#7540EE] leading-tight tracking-wide"
                      >
                        {{ t(item.sup) }}
                      </sup>
                    </h3>
                    <p class="tracking-wide text-[#DEDEDE]">
                      {{ t(item.description) }}
                    </p>
                  </div>
                </div>
              </div>
            </UCarousel>
          </div>
        </ClientOnly>

        <div
          class="pt-10 px-5 lg:px-0 flex flex-col gap-6 lg:flex-row lg:gap-10 lg:pl-6.5 lg:pr-5.5 lg:pt-15 text-center lg:text-left lg:pb-6.5"
        >
          <p
            class="text-xl tracking-wide text-[#7540EE] font-medium text-center lg:text-left lg:text-2xl"
          >
            {{ t('economic-sustainability') }}
          </p>
          <div class="flex flex-col gap-4 lg:flex-row lg:gap-9">
            <div
              v-for="item in payoutSections"
              :key="item.title"
              class="flex flex-col gap-2.5"
            >
              <h3 class="text-lg tracking-wide">
                {{ t(item.title) }}
              </h3>
              <p class="font-light tracking-wide text-[#DEDEDE]">
                {{ t(item.description) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="before:bg-[linear-gradient(0,rgba(21,21,21,0)_0%,#151515_100%)] before:absolute before:top-0 before:w-full before:h-27 before:-z-1 after:bg-[linear-gradient(180deg,rgba(21,21,21,0)_0%,#151515_100%)] after:h-113.5 after:w-full after:absolute after:bottom-0 after:-z-1"
      >
        <NuxtImg
          src="/images/engagement/token-architecture-bg.png"
          class="absolute top-0 w-full h-full object-cover -z-2"
        />
      </div>
    </div>

    <div class="pt-6 lg:pt-11.5 relative">
      <h2 class="font-bold text-xl lg:text-2xl tracking-wide text-center">
        {{ t('core-mechanics') }}
      </h2>

      <div
        class="flex flex-col justify-center pt-7.5 lg:pt-5.5 md:flex-row gap-8 lg:gap-4 px-4"
      >
        <div
          v-for="(step, i) in payoutSteps"
          :key="step.title"
          class="before:rounded-2xl before:z-1 common-gradient min-h-80 md:max-w-90 lg:h-114.5 flex flex-col justify-between rounded-2xl bg-[#0D0D0D] py-7 pl-9 pr-17 lg:px-9 relative overflow-hidden"
          :class="{
            'before:bg-[linear-gradient(132.8deg,#7540ee_22.34%,rgba(67,37,136,0)_89.96%)]':
              i === 0,
            'before:bg-[linear-gradient(301.42deg,#7540EE_0.29%,rgba(67,37,136,0)_82.8%)]':
              i === 1,
          }"
        >
          <div class="flex flex-col gap-5 relative z-1">
            <h3
              class="text-[#7740EE] text-base lg:text-lg tracking-wide font-medium"
            >
              {{ t(step.title) }}
            </h3>

            <div class="flex flex-col gap-1.5">
              <h4 class="text-lg lg:text-[1.375rem] tracking-wide font-medium">
                {{ t(step.subtitle) }}
              </h4>
              <p class="tracking-wide text-[#DEDEDE]">
                {{ t(step.description) }}
              </p>
            </div>
          </div>

          <ul
            v-if="step.options"
            class="flex flex-col gap-5 relative z-1"
            :class="{ 'lg:pb-7': step.id === 1 }"
          >
            <li
              v-for="option in step.options"
              :key="option"
              class="text-[#7540EE] tracking-wide text-sm"
            >
              {{ t(option) }}
            </li>
          </ul>

          <NuxtImg
            v-if="step.id === 1"
            src="/images/engagement/long-token.png"
            class="absolute w-35 -right-7 -bottom-5 lg:w-38.5 lg:-right-7.5 lg:bottom-3"
          />
        </div>
      </div>

      <NuxtImg
        src="/images/engagement/core-mechanics-bg.png"
        class="absolute w-full max-w-327.5 mx-auto left-0 right-0 h-111.5 top-16 -z-1 object-cover"
      />
    </div>

    <div class="pt-10.5 lg:pt-17.5 pb-10 lg:pb-15">
      <div
        class="flex flex-col justify-between px-5 max-w-284 mx-auto min-[1200px]:flex-row min-[1200px]:px-0"
      >
        <div
          class="flex flex-col gap-3 min-[1200px]:max-w-76 min-[1200px]:pt-15 text-center min-[1200px]:text-left"
        >
          <h2 class="text-2xl tracking-wide font-extrabold">
            {{ t('technical-excellence') }}
          </h2>
          <p class="text-[#DEDEDE] tracking-wide">
            {{ t('technical-excellence-description') }}
          </p>
        </div>

        <div
          class="flex flex-row flex-wrap max-w-192.5 gap-y-2 gap-x-2.5 pt-13.5 min-[1200px]:pt-0 mx-auto min-[1200px]:mx-0 relative before:content-[''] before:absolute after:content-[''] after:absolute after:w-full after:h-full before:w-full before:h-full asd"
        >
          <div
            v-for="feature in roadmapFeatures"
            :key="feature.descriptionKey"
            class="border-1 border-solid border-[#2B2B2B] bg-[#0000001A] rounded-xl w-full lg:w-95 lg:h-52.5 pt-6 pl-6 pr-12.5 pb-7.5 lg:py-6 lg:pr-9"
          >
            <div class="flex flex-col items-start gap-2 lg:gap-2.5">
              <NuxtImg :src="feature.icon" class="h-13.5 object-contain" />
              <p
                class="tracking-wide font-light text-[#DEDEDE] text-sm lg:text-base"
              >
                {{ t(feature.descriptionKey) }}
              </p>
            </div>
          </div>

          <!-- <NuxtImg
            src="/images/engagement/bg.png"
            class="w-full h-full absolute top-0 -left-69 -z-1"
          /> -->
        </div>
      </div>
    </div>

    <div class="relative">
      <NuxtImg
        src="/images/engagement/bg-left.png"
        class="top-80 absolute left-0 -z-1"
      />

      <NuxtImg
        src="/images/engagement/bg-right.png"
        class="top-50 lg:top-100 absolute right-0 -z-1"
      />

      <div
        class="flex flex-col justify-center gap-15 lg:flex-row-reverse lg:justify-between items-center max-w-296 mx-auto pt-10 lg:pt-18 px-5 min-[1230px]:px-0"
      >
        <NuxtImg src="/images/engagement/vote.png" class="w-75 lg:w-98" />

        <div class="flex flex-col gap-5.5 lg:gap-4.5">
          <div
            class="flex flex-col gap-8.5 lg:gap-20.5 lg:max-w-144 text-center lg:text-left"
          >
            <div class="flex flex-col gap-6.5 pl-2">
              <div class="flex flex-col gap-3.5">
                <p class="lg:text-xl tracking-wide text-[#7540ee] uppercase">
                  {{ t('ecosystem.benefits.sectionTitle') }}
                </p>
                <p
                  class="font-extrabold text-xl lg:text-2xl tracking-wide uppercase"
                >
                  <span class="text-[#7540EE]">LONG</span>
                  {{ t('ecosystem.benefits.mainTitle') }}
                </p>
              </div>
              <p>
                {{ t('ecosystem.benefits.description') }}
              </p>
            </div>
            <p class="text-xl tracking-wide uppercase text-[#7540EE]">
              {{ t('ecosystem.benefits.venueBenefits') }}
            </p>
          </div>

          <div
            class="flex flex-col w-full gap-6 justify-center lg:justify-start sm:flex-row sm:mx-auto flex-wrap sm:max-w-173 sm:gap-2"
          >
            <div
              v-for="(benefit, i) in benefits"
              :key="i"
              class="border-[#2B2B2B] border-1 border-solid sm:max-w-56 rounded-lg flex flex-col gap-3.5 w-full py-3 pr-2 pl-3.5"
            >
              <div class="flex flex-row items-center gap-1.5">
                <component :is="benefit.icon" :class="benefit.iconClass" />
                <p
                  :class="
                    locale === 'en' ? 'tracking-tight' : 'tracking-wide text-sm'
                  "
                >
                  {{ benefit.title }}
                </p>
              </div>
              <p
                class="text-[#B9B9B9]"
                :class="
                  locale === 'en'
                    ? 'tracking-tight leading-4.5'
                    : 'tracking-wide  text-sm'
                "
              >
                <template v-if="benefit.i18nKey">
                  <span class="text-[#7540EE]">
                    {{ benefit.i18nKey }}
                  </span>
                </template>
                {{ benefit.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <EngagementRolesOrbit />

    <div class="pt-11 lg:pt-0">
      <p
        class="text-xl font-extrabold tracking-wide pb-6.5 lg:pb-12 text-center"
      >
        {{ t('long-term-vision') }}
      </p>

      <div class="relative mx-auto max-w-169 lg:max-w-290.5 overflow-hidden">
        <div
          class="w-0.5 h-full absolute top-2.5 left-5 -z-1 lg:w-full lg:h-0.5 min-[1230px]:!left-0 lg:top-1.25 lg:left-5 bg-[linear-gradient(180deg,#4d61fd_0%,#bb2bbf_79.81%,rgba(187,43,191,0)_98.82%)] lg:bg-[linear-gradient(90deg,#4D61FD_0%,#BB2BBF_79.81%,rgba(187,43,191,0)_98.82%)]"
        />

        <div
          class="flex flex-col gap-8.5 pr-11.5 lg:flex-row lg:px-5 min-[1230px]:!px-0"
        >
          <div
            v-for="(phase, i) in roadmap"
            :key="i"
            class="flex flex-col gap-6"
          >
            <div
              class="flex flex-row items-center pl-3.5 lg:flex-col lg:items-start lg:pl-0"
            >
              <div
                class="w-3 h-3 rounded-full border-solid border-1 border-[#161616]"
                :class="{ 'bg-[#AD32C6]': i === 2, 'bg-[#7540EE]': i !== 2 }"
              />
              <p
                class="text-xl font-extrabold tracking-wide pl-4 lg:pl-0 lg:pt-2.5"
                style="
                  background: linear-gradient(90deg, #4d61fd 0%, #bb2bbf 98.82%)
                    text;
                  -webkit-text-fill-color: transparent;
                "
              >
                {{ t(phase.year) }}
              </p>
            </div>

            <div class="pl-16.5 text-[#CCCCCC] flex flex-col gap-1.5 lg:pl-5">
              <p class="uppercase text-xl tracking-wide">
                {{ t(phase.title) }}
              </p>
              <ul class="flex flex-col gap-2.5">
                <li
                  v-for="(point, j) in phase.points"
                  :key="j"
                  class="relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-[#7540EE]"
                >
                  <p
                    class="pl-4 font-light tracking-wider text-[#DEDEDE] lg:leading-5"
                  >
                    {{ t(point) }}
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@reference "tailwindcss";

.tap-earn-steps-gradient {
  &::before {
    @apply rounded-full;
    background: linear-gradient(107.92deg, #4d61fd 37.78%, #bb2bbf 106.06%);
  }
}

.tap-earn-steps-gradient {
  &::after {
    @apply rounded-full;
    background: linear-gradient(90deg, #4d61fd 2.16%, #bb2bbf 104.19%);
  }
}

.scope {
  transform-style: preserve-3d;
  animation: slid 30s linear infinite;
}

.scope span {
  transform-origin: center;
  transform-style: preserve-3d;
}
.scope span img {
  transition: 2s;
}

@keyframes slid {
  0% {
    transform: perspective(1000px) rotateY(0deg);
  }
  100% {
    transform: perspective(1000px) rotateY(360deg);
  }
}

.bg-tap-earn-gradient {
  background: linear-gradient(90deg, #4d61fd 2.16%, #bb2bbf 104.19%);
}

.grid-fade {
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  background: radial-gradient(
    ellipse at 50% 50%,
    rgba(21, 21, 21, 0) 0%,
    rgba(21, 21, 21, 1) 80%
  );
}

.grid-lines {
  width: 100%;
  height: 200%;
  background-image: linear-gradient(
      to right,
      rgba(117, 65, 237, 1) 1px,
      transparent 0
    ),
    linear-gradient(to bottom, rgba(117, 65, 237, 1) 1px, transparent 0);
  background-size: 45px 30px;
  background-repeat: repeat;
  transform-origin: 100% 0 0;
  animation: play 15s linear infinite;
}

@keyframes play {
  0% {
    transform: rotateX(45deg) translateY(-50%);
  }
  100% {
    transform: rotateX(45deg) translateY(0);
  }
}
</style>
